import { useToast } from '@chakra-ui/react';
import { compositionGroupApi } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ServerError } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSignalSubscriptionEvents } from '@texas/components/shared/hooks/useSignalSubscriptionEvents';
import { compositionEvents } from '@texas/components/shared/composition/events';
import { compositionGroupEvents } from '@texas/components/shared/compositionGroup/events';
import { useSetDimensionValueEvent } from '@texas/components/shared/compositionGroup/useSetDimensionValueEvent';

interface CompositionLocationData {
  variantId: number | undefined;
  supplierId?: number;
  branchId?: number;
  cellX?: number;
  cellY?: number;
  template?: boolean;
}

export function useTemplatesOverview() {
  const {
    refetch,
    data: versions,
    loading: versionsLoading,
    set: setOptions,
  } = useApiResource(compositionGroupApi.templatesOverview);
  const toast = useToast();
  const { t } = useTranslation();
  const locationData = useRef<CompositionLocationData>({
    variantId: undefined,
    template: true,
  });

  useSetDimensionValueEvent(setOptions);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const refetchTemplates = useCallback(() => {
    refetch();
  }, [refetch]);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    // We do no refetch here since dimensions are changed using auto update which would result in a lot of calls
    return compositionEvents.onDimensionValueChanged.sub(({ groupId }) => {
      setOptions((x) =>
        x
          ? x.map((o) => {
              if (o.id === groupId) {
                return { ...o, isReady: false };
              }

              return o;
            })
          : [],
      );
    });
  }, [setOptions]);

  useEffect(() => {
    return compositionEvents.onDimensionGroupSet.sub(() => refetchTemplates());
  }, [refetchTemplates]);

  useSignalSubscriptionEvents({
    triggerOnEvent: refetchTemplates,
    signalEvents: [
      compositionGroupEvents.onArchived,
      compositionGroupEvents.onRestored,
      compositionGroupEvents.onCreate,
      compositionGroupEvents.onCopy,
      compositionEvents.fileUpdated,
      compositionEvents.onRename,
      compositionGroupEvents.onRename,
      compositionEvents.onMaterialAdded,
      compositionEvents.onMaterialChanged,
      compositionEvents.onMaterialRemoved,
      compositionEvents.onAdd,
      compositionEvents.onArchived,
      compositionEvents.onRestored,
      compositionGroupEvents.onLayoutChanged,
      compositionGroupEvents.onMarkReady,
      compositionGroupEvents.onMarkNotReady,
    ],
  });

  const createGroupRequest = useCallback(async () => {
    setIsCreating(true);
    await request(
      compositionGroupApi.addGroup,
      [{ template: true, variantId: undefined }],
      (_) => {
        compositionGroupEvents.onCreate.dispatch();
      },
      (error: ServerError) => {
        toast({
          title: t('general.createFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );
    setIsCreating(false);
  }, [t, toast]);

  const copyGroup = useCallback(
    async (props: {
      fromId: number;
      location?: CompositionLocationData;
      basedOnId?: number;
    }) => {
      setIsCreating(true);
      await request(
        compositionGroupApi.copyGroup,
        [
          props.fromId,
          {
            ...(props.location ?? locationData.current),
            replaceCompositionGroupId: undefined,
            basedOnId: props.basedOnId,
          },
        ],
        (_) => {
          compositionGroupEvents.onCopy.dispatch();
          toast({
            title: t('general.copied'),
            status: 'success',
            isClosable: true,
          });
        },
        (error: ServerError) => {
          toast({
            title: t('general.copyFailed'),
            description: error.message,
            status: 'error',
            isClosable: true,
          });
        },
      );
      setIsCreating(false);
    },
    [t, toast],
  );

  const archiveGroupRequest = useCallback(
    async (id: number) => {
      await request(
        compositionGroupApi.archive,
        [id],
        (_) => {
          compositionGroupEvents.onArchived.dispatch();
        },
        (error: ServerError) => {
          toast({
            title: t('general.archiveFailed'),
            description: error.message,
            status: 'error',
            isClosable: true,
          });
        },
      );
    },
    [t, toast],
  );

  const mainProductApproval = useCallback(
    (id: number) => {
      return versions?.find((v) => v.id === id)?.mainProductApproval;
    },
    [versions],
  );

  return {
    versions,
    versionsLoading,
    createGroupRequest,
    copyGroup,
    mainProductApproval,
    locationData,
    archiveGroupRequest,
    refetchOptions: refetchTemplates,
    isCreating,
  };
}
